import { Injectable, inject, signal } from '@angular/core';
import { format } from 'date-fns/format';
import { finalize, tap } from 'rxjs';

import {
  RoomCalendarEventsModel,
  RoomsApiService,
  utcDateToTimezoneDisplayDate,
} from '@bookly/shared';

@Injectable()
export class RoomBookingCalendarService {
  readonly #roomsApiService = inject(RoomsApiService);
  readonly #roomAvailabilitySignal = signal<RoomCalendarEventsModel[]>([]);
  readonly #isCalendarDataLoadingSignal = signal<boolean>(false);

  get roomAvailability() {
    return this.#roomAvailabilitySignal.asReadonly();
  }

  get isCalendarDataLoading() {
    return this.#isCalendarDataLoadingSignal.asReadonly();
  }

  public getRoomAvailability(
    calendarId: string,
    dateFrom: string,
    dateTo: string,
    semiPrivate: boolean,
    siteTimeZone: string | null
  ) {
    this.#isCalendarDataLoadingSignal.set(true);
    return this.#roomsApiService
      .getRoomAvailability(calendarId, dateFrom, dateTo, semiPrivate)
      .pipe(
        tap(availability => {
          this.#roomAvailabilitySignal.set(
            this.#toSiteTimezoneAvailability(availability, siteTimeZone)
          );
        }),
        finalize(() => this.#isCalendarDataLoadingSignal.set(false))
      );
  }

  public setDraggable(bookingId: number) {
    const availability = this.roomAvailability();
    const updated = availability.map(element => {
      if (element.meta?.bookingId === bookingId) {
        element.draggable = true;
      }
      return element;
    });
    this.#roomAvailabilitySignal.set(updated);
  }

  #toSiteTimezoneAvailability(
    availability: RoomCalendarEventsModel[],
    siteTimeZone: string | null
  ): RoomCalendarEventsModel[] {
    return availability.map(event => {
      if (event.allDay || !(event.start instanceof Date)) {
        return event;
      }
      const start = utcDateToTimezoneDisplayDate(event.start, siteTimeZone);
      const end = utcDateToTimezoneDisplayDate(
        event.end ?? event.start,
        siteTimeZone
      );
      const meta = event.meta
        ? {
            ...event.meta,
            timeStart: format(start, 'HH:mm'),
            timeEnd: format(end, 'HH:mm'),
          }
        : undefined;

      return {
        ...event,
        start,
        end,
        meta,
      };
    });
  }
}
